// QR Code Traceability Page
// Displays milk batch information when QR code is scanned
// URL format: /trace/:batchId

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { motion } from 'framer-motion';
import './Traceability.css';

const Traceability = () => {
  const { batchId } = useParams();
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (batchId) {
      loadBatchData(batchId);
    } else {
      setError('No batch ID provided');
      setLoading(false);
    }
  }, [batchId]);

  const loadBatchData = async (id) => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch from milk_batches collection first
      const batchDocRef = doc(db, 'milk_batches', id);
      const batchDoc = await getDoc(batchDocRef);

      if (batchDoc.exists()) {
        const data = batchDoc.data();
        setBatchData({
          id: batchDoc.id,
          ...data,
          // Format dates
          milkingDate: data.milkingDate?.toDate ? data.milkingDate.toDate() : new Date(data.milkingDate),
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      } else {
        // Fallback: try to find in milkLogs collection
        const milkLogRef = doc(db, 'milkLogs', id);
        const milkLogDoc = await getDoc(milkLogRef);
        
        if (milkLogDoc.exists()) {
          const data = milkLogDoc.data();
          setBatchData({
            id: milkLogDoc.id,
            batchId: id,
            milkingDate: data.date ? new Date(data.date) : (data.createdAt?.toDate ? data.createdAt.toDate() : new Date()),
            animalGroup: data.animalGroup || 'N/A',
            housingArea: data.housingArea || 'N/A',
            fatPercentage: data.fatPercentage || data.fat || 'N/A',
            snfPercentage: data.snfPercentage || data.snf || 'N/A',
            somaticCellCount: data.somaticCellCount || data.scc || 'N/A',
            antibioticTest: data.antibioticTest || 'Pass',
            labReportUrl: data.labReportUrl || null,
            source: 'milkLog'
          });
        } else {
          setError('Batch not found. Please verify the QR code is correct.');
        }
      }
    } catch (err) {
      console.error('Error loading batch data:', err);
      setError('Failed to load batch information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="traceability-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading traceability information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="traceability-page">
        <div className="container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Unable to Load Batch Information</h2>
            <p>{error}</p>
            <a href="/" className="btn btn-primary">Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="traceability-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="traceability-card"
        >
          <div className="traceability-header">
            <h1>Milk Traceability Report</h1>
            <p className="batch-id">Batch ID: {batchData?.batchId || batchData?.id}</p>
          </div>

          <div className="traceability-content">
            {/* Milking Information */}
            <section className="trace-section">
              <h2>📅 Milking Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Milking Date & Time</span>
                  <span className="info-value">{formatDate(batchData?.milkingDate)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Animal Group</span>
                  <span className="info-value">{batchData?.animalGroup || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Housing Area</span>
                  <span className="info-value">{batchData?.housingArea || 'N/A'}</span>
                </div>
              </div>
            </section>

            {/* Quality Metrics */}
            <section className="trace-section">
              <h2>🔬 Quality Metrics</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Fat Percentage</span>
                  <span className="info-value highlight">
                    {batchData?.fatPercentage ? `${batchData.fatPercentage}%` : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">SNF Percentage</span>
                  <span className="info-value highlight">
                    {batchData?.snfPercentage ? `${batchData.snfPercentage}%` : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Somatic Cell Count</span>
                  <span className="info-value">
                    {batchData?.somaticCellCount ? `${batchData.somaticCellCount}` : 'N/A'}
                  </span>
                </div>
              </div>
            </section>

            {/* Safety & Testing */}
            <section className="trace-section">
              <h2>✅ Safety & Testing</h2>
              <div className="safety-badge">
                <div className={`badge ${batchData?.antibioticTest === 'Pass' ? 'pass' : 'pending'}`}>
                  {batchData?.antibioticTest === 'Pass' ? '✓' : '⏳'}
                </div>
                <div className="safety-info">
                  <h3>Antibiotic Residue Test</h3>
                  <p className={batchData?.antibioticTest === 'Pass' ? 'pass-text' : 'pending-text'}>
                    {batchData?.antibioticTest === 'Pass' 
                      ? 'Zero detectable antibiotic residue - Verified safe' 
                      : batchData?.antibioticTest || 'Test pending'}
                  </p>
                  {batchData?.labReportUrl && (
                    <a href={batchData.labReportUrl} target="_blank" rel="noopener noreferrer" className="lab-report-link">
                      View Full Lab Report →
                    </a>
                  )}
                </div>
              </div>
            </section>

            {/* Additional Information */}
            {(batchData?.animalNames || batchData?.notes) && (
              <section className="trace-section">
                <h2>📝 Additional Information</h2>
                {batchData.animalNames && (
                  <div className="info-item">
                    <span className="info-label">Source Animals</span>
                    <span className="info-value">{batchData.animalNames}</span>
                  </div>
                )}
                {batchData.notes && (
                  <div className="info-item">
                    <span className="info-label">Notes</span>
                    <span className="info-value">{batchData.notes}</span>
                  </div>
                )}
              </section>
            )}

            {/* Verification */}
            <section className="trace-section verification">
              <div className="verification-badge">
                <div className="verification-icon">✓</div>
                <div>
                  <h3>Verified by PharmWealth</h3>
                  <p>This batch has been verified and meets our quality standards</p>
                </div>
              </div>
            </section>
          </div>

          <div className="traceability-footer">
            <a href="/" className="btn btn-secondary">Learn More About PharmWealth</a>
            <a href="/premium-milk" className="btn btn-primary">View Our Products</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Traceability;

